import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { AuthenticationResult, InteractionRequiredAuthError, InteractionStatus, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Claim } from '../model/claim.model';
import { protectedResources } from 'src/app/auth-config';
import { SecurityRequestService } from './security-request.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly _destroying$ = new Subject<void>();
  public valueSelected = new BehaviorSubject<any>(new Claim());

  constructor(
    private msalService: MsalService,
    private router: Router,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalBroadcastService: MsalBroadcastService,
    private httpClient: HttpClient
  ) {
    const claims = this.getClaims();
    this.valueSelected.next(claims);
  }

  public logout(): void {

    this.msalService.logout().subscribe(
      success => {
        console.log(success);
        this.router.navigate([`/`]);
      },
      error => console.error(error)
    );
  }

  public login(): void {
    this.checkAndSetActiveAccount();

    if (this.hasActiveAccount()) {
      this.router.navigate([`/welcome`]);
      return;
    }

    const loginRequest = this.msalGuardConfig.authRequest
      ? { ...this.msalGuardConfig.authRequest } as RedirectRequest
      : undefined;

    this.msalService.loginRedirect(loginRequest).subscribe({
      error: error => console.error(error)
    });
  }

  public checkAndSetActiveAccount(): void {

    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  public hasActiveAccount(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  public isAuthenticated(): Observable<boolean> {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length === 0) {
      return of(false);
    }

    return from(this.msalService.acquireTokenSilent({ scopes: ['user.read'] })).pipe(
      map((response: AuthenticationResult) => {
        if (!response.accessToken) {
          return false;
        }

        // Verifica a expiração real do token
        const expiration = response.expiresOn ? response.expiresOn.getTime() : 0;
        const now = new Date().getTime();

        return expiration > now; // Retorna true apenas se o token ainda for válido
      }),
      catchError(error => {
        // Se o erro for relacionado à sessão expirada ou login necessário, limpar cache
        if (error instanceof InteractionRequiredAuthError || error.errorCode === 'invalid_grant') {
          this.logout();
        }

        return of(false);
      })
    );
  }

  public AcquireTokenSilent(): Observable<any> {

    if (this.msalService.instance.getActiveAccount() == null) {
      this.checkAndSetActiveAccount();
    }

    return this.msalService.acquireTokenSilent(protectedResources.todoListApi).pipe(
      catchError((error: any) => {
        const errorCode = (error?.errorCode || '').toString().toLowerCase();
        const shouldRedirectToLogin =
          error instanceof InteractionRequiredAuthError ||
          error?.status === 401 ||
          ['invalid_grant', 'interaction_required', 'login_required', 'consent_required'].includes(errorCode);

        if (shouldRedirectToLogin) {
          this.toClean();
          this.router.navigateByUrl('/login');

          const loginRequest = this.msalGuardConfig.authRequest
            ? { ...this.msalGuardConfig.authRequest } as RedirectRequest
            : undefined;

          this.msalService.loginRedirect(loginRequest).subscribe({
            error: loginError => console.error(loginError)
          });
        }

        return throwError(() => error);
      })
    );
  }

  public AcquireTokenSilentGraph(): Observable<any> {

    if (this.msalService.instance.getActiveAccount() == null) {
      this.checkAndSetActiveAccount();
    }

    return of({
      scopes: [
        "https://graph.microsoft.com/User.Read"
      ]
    }).pipe(
      switchMap(request => this.msalService.acquireTokenSilent(request))
    )
  }

  public getAuthorization(): void {

    of({})
      .pipe(
        switchMap(request => this.AcquireTokenSilent()),
        map(request => {
          return {
            headers: new HttpHeaders().set('Authorization', `Bearer ${request.accessToken}`)
          };
        }),
        switchMap(options => this.httpClient.request("GET", `${environment.bffApi}/authorization`, options)),
        catchError(error => {
          if (error.status === 401) {
            this.toClean();
            return of(null);
          }
          return of(null);
        })
      ).subscribe(
        claims => {
          if (!claims || typeof claims !== 'object' || !('Modules' in claims)) {
            this.toClean();
            this.valueSelected.next(new Claim());
            return;
          }

          localStorage.setItem('claims', JSON.stringify(claims));
          this.valueSelected.next(claims);

          if (this.router.url === "/login")
            this.router.navigate([`/welcome`]);

        },
        error => console.error(error)
      )
  }

  public getClaims(): Claim {
    var _claims = localStorage.getItem("claims");

    if (_claims == null || _claims === 'null' || _claims === 'undefined')
      return new Claim();

    try {
      const parsedClaims = JSON.parse(_claims);

      if (!parsedClaims || typeof parsedClaims !== 'object')
        return new Claim();

      return Object.assign(new Claim(), parsedClaims);
    } catch {
      return new Claim();
    }
  }

  public toClean(): void {
    localStorage.removeItem("claims");
    this.valueSelected.next(new Claim());
  }
}
